<!-- 
화면명 : UCText.vue
작성일 : 2025-07-01
설명   : 텍스트 컨트롤
-->
<template>
  <div class="flex flex-col">
    <div class="flex items-center space-x-1">
      <img src="@/assets/img/blue-circle.png" class="w-3 h-3" v-if="label" />

      <label
        class="text-blueGray-600 text-sm font-semibold whitespace-nowrap"
        :style="{ width: widthLabelStyle }"
        v-if="label"
      >
        {{ label }}
        <span v-if="requiredAsterisk" class="text-red-500 ml-0.5">*</span>
      </label>

      <div class="relative w-full">
        <input
          :value="modelValue"
          :type="setType"
          :placeholder="placeholder"
          :disabled="disabled"
          class="flex-1 px-3 py-1 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm border border-blueGray-300 shadow-sm outline-none focus:outline-none focus:ring-2 focus:ring-lightBlue-500"
          :class="inputClasses"
          :style="{ width: widthStyle }"
          @input="onInput"
          @blur="onBlur"
        />

        <!-- 아이콘 버튼 -->
        <button
          type="button"
          class="absolute inset-y-0 right-3 flex items-center text-gray-500"
          v-if="type === 'password' ? true : false"
          @click="togglePassword"
        >
          <i
            :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
            class="text-xl"
          ></i>
        </button>
      </div>
    </div>

    <!-- 에러 메시지 -->
    <p v-if="firstError" class="mt-1 text-sm text-red-600">
      {{ firstError }}
    </p>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  defineExpose,
  computed,
  watch,
} from "vue";

const props = defineProps({
  label: { type: String, default: "" },
  modelValue: { type: String, default: "", required: true },
  placeholder: { type: String, default: "" },
  type: { type: String, default: "text" },
  disabled: { type: Boolean, default: false },
  validateOn: { type: String, default: "blur" }, // 'input' | 'blur' | 'submit'
  requiredAsterisk: { type: Boolean, default: false },
  rules: { type: Array, default: () => [] },
  width: { type: [String, Number], default: "100%" },
  widthLabel: { type: [String, Number], default: "100%" },
});

const emit = defineEmits(["update:modelValue", "valid", "invalid"]);

watch(
  () => props.modelValue,
  (v) => {
    if (props.validateOn === "submit" && internalErrors.value.length)
      runValidation(v);
  }
);

const setType = computed(() => {
  let result;

  if (props.type === "password") {
    result = showPassword.value ? "text" : "password";
  } else {
    result = props.type;
  }
  return result;
});

const inputClasses = computed(() => {
  if (isError.value) {
    return "border-red-500 focus:ring-red-200 focus:border-red-500";
  }
  return "border-gray-300 focus:ring-blue-200 focus:border-blue-500";
});

const showPassword = ref(false);
const togglePassword = () => {
  showPassword.value = !showPassword.value;
  // console.log("togglePassword showPassword.value -> ", showPassword.value);
};

// width 계산
const widthStyle = computed(() => {
  return typeof props.width === "number" ? `${props.width}px` : props.width;
});

const widthLabelStyle = computed(() => {
  return typeof props.widthLabel === "number"
    ? `${props.widthLabel}px`
    : props.widthLabel;
});

const internalErrors = ref([]);
const touched = ref(false);

const runValidation = (val) => {
  // console.log("runValidation val ->", val);

  const arr = [];
  for (const rule of props.rules) {
    if (typeof rule === "function") {
      const res = rule(val);
      if (res !== true)
        arr.push(typeof res === "string" ? res : "Invalid value");
    } else if (rule !== true) {
      // 문자열이면 그대로 에러 메시지 취급
      arr.push(typeof rule === "string" ? rule : "Invalid value");
    }
  }
  // console.log("runValidation arr", arr);

  internalErrors.value = arr;

  if (arr.length) {
    emit("invalid", arr);
  } else {
    emit("valid", true);
  }

  return arr.length === 0;
};

const onInput = (e) => {
  const val = e.target.value;
  emit("update:modelValue", val);

  if (props.validateOn === "input") runValidation(val);
};

const onBlur = () => {
  touched.value = true;

  if (props.validateOn === "blur") runValidation(props.modelValue);
};

// 부모에서 수동 호출용
const validate = () => runValidation(props.modelValue);
const reset = () => {
  internalErrors.value = [];
  touched.value = false;
};
defineExpose({ validate, reset });

const isError = computed(() =>
  props.validateOn === "submit"
    ? internalErrors.value.length > 0
    : touched.value && internalErrors.value.length > 0
);

const firstError = computed(() => {
  // console.log("computed firstError", internalErrors.value[0]);

  const result = isError.value ? internalErrors.value[0] : "";
  // console.log("result", result);

  return result;
});
const allErrors = computed(() => (isError.value ? internalErrors.value : []));
</script>
